import { Link } from '@tanstack/react-router'
import { BUTTON } from '../lib/admin.tsx'
import {
  activityById,
  ago,
  CampaignTable,
  daysUntil,
  Live,
  Spark,
  statusOf,
  useAdminCampaigns,
  useAgreements,
  useToday,
  type Activity,
  type AdminCampaign,
  type Agreement,
  type FeedItem,
} from '../lib/platform.tsx'

/**
 * Today — the platform admin's home (docs/decisions.md 2026-10-01). Activity
 * first, because "money stems from happy functioning customers": who is
 * working right now, what came in today, what needs the owner, and a feed of
 * what happened. Money lives on Billing, not here.
 */

type CampaignTab = 'team' | 'licenses' | 'paperwork'

interface Attention {
  key: string
  urgent: boolean
  title: string
  text: string
  action: string
  campaignId: string
  tab: CampaignTab
}

/** What needs the owner, from what the campaigns and agreements already say. */
function needsYou(campaigns: AdminCampaign[], agreements: Agreement[], activity: Map<string, Activity>): Attention[] {
  const out: Attention[] = []
  for (const c of campaigns) {
    if (c.status !== 'active') continue
    if (!c.attestation_signed_at) {
      out.push({
        key: `att-${c.campaign_id}`, urgent: true, campaignId: c.campaign_id, tab: 'paperwork',
        title: `${c.name}: no signed attestation`,
        text: 'Nobody on this campaign can use it until the permitted-use attestation is recorded.',
        action: 'Record it',
      })
      continue
    }
    if (c.purchases === 0 && !c.may_report) {
      out.push({
        key: `buy-${c.campaign_id}`, urgent: true, campaignId: c.campaign_id, tab: 'licenses',
        title: `${c.name}: nothing purchased`,
        text: 'Not on any agreement, so its team can sign in but cannot do anything.',
        action: 'Add licenses',
      })
    }
    if (c.people === 0) {
      out.push({
        key: `team-${c.campaign_id}`, urgent: true, campaignId: c.campaign_id, tab: 'team',
        title: `${c.name}: nobody on the team`,
        text: 'Set up, but no one has been added. Start with the campaign manager.',
        action: 'Add someone',
      })
    }
    const days = c.election_date ? daysUntil(c.election_date) : null
    if (c.may_cut && days !== null && days >= 0 && days <= 7) {
      out.push({
        key: `el-${c.campaign_id}`, urgent: false, campaignId: c.campaign_id, tab: 'team',
        title: `${c.name}: election ${days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}`,
        text: 'Cutting stops on election day. Reports stay open for 30 days after.',
        action: 'Open',
      })
    }
    const last = activity.get(c.campaign_id)?.last_active ?? null
    const quietDays = last ? (Date.now() - new Date(last).getTime()) / 86_400_000 : Infinity
    if (c.may_cut && c.people > 0 && quietDays >= 7) {
      out.push({
        key: `quiet-${c.campaign_id}`, urgent: false, campaignId: c.campaign_id, tab: 'team',
        title: `${c.name}: quiet ${last ? `since ${ago(last)}` : '— nobody has used it yet'}`,
        text: 'Nobody on the team has done anything lately. Worth a call to see if they are stuck.',
        action: 'Open',
      })
    }
  }
  for (const a of agreements) {
    if (a.status !== 'active' || a.campaigns.length === 0) continue
    const first = a.campaigns[0]!
    const who = a.campaigns.length > 1 ? a.name : first.name
    if (a.dialer_seats > 0 && a.phones_in_use >= a.dialer_seats) {
      out.push({
        key: `seats-${a.agreement_id}`, urgent: false, campaignId: first.campaign_id, tab: 'licenses',
        title: `${who}: phone seats full`,
        text: `${a.phones_in_use} of ${a.dialer_seats} in use. More callers wait for a free seat.`,
        action: 'Add seats',
      })
    }
    if (a.walk_licenses !== null && a.walk_licenses > 0 && a.walk_today >= a.walk_licenses) {
      out.push({
        key: `walk-${a.agreement_id}`, urgent: false, campaignId: first.campaign_id, tab: 'licenses',
        title: `${who}: walk licenses used up today`,
        text: `${a.walk_today} of ${a.walk_licenses} phones signed in today. The next canvasser is refused.`,
        action: 'Add licenses',
      })
    }
    if (a.list_credits !== null && a.lists_used >= a.list_credits) {
      out.push({
        key: `lists-${a.agreement_id}`, urgent: false, campaignId: first.campaign_id, tab: 'licenses',
        title: `${who}: out of list cuts`,
        text: `Used all ${a.list_credits}. Their next list will be refused.`,
        action: 'Add lists',
      })
    }
  }
  // Blocking problems first.
  return out.sort((x, y) => Number(y.urgent) - Number(x.urgent))
}

function feedText(f: FeedItem): string {
  const who = f.actor_email ?? 'Someone'
  const d = f.detail
  switch (f.event_type) {
    case 'data.list.export.completed':
      return `${who} cut a list: ${Number(d.rows ?? 0).toLocaleString()} rows`
    case 'data.contacts.imported':
      return `${who} imported results (${Number(d.recorded ?? 0).toLocaleString()} recorded)`
    case 'campaign.provisioned':
      return d.attested ? 'Campaign set up' : 'Campaign set up, waiting on its attestation'
    case 'campaign.org.provisioned':
      return `New customer: ${String(d.name ?? '')}`
    case 'campaign.attestation.recorded':
      return 'Attestation recorded'
    case 'campaign.walk.assigned':
      return `${who} assigned a walk`
    case 'billing.agreement.created':
      return `Agreement recorded: ${String(d.name ?? '')}`
    case 'auth.invitation.accepted':
      return `${who} joined the team`
    case 'auth.canvasser.created':
      return `${who} added canvasser ${String(d.username ?? '')}`
    default:
      return f.event_type
  }
}

function feedTime(iso: string): string {
  const t = new Date(iso)
  const today = new Date().toDateString() === t.toDateString()
  return today
    ? t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function Tile({ label, value, hint, live, children }: { label: string; value: number; hint: string; live?: boolean; children?: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg border border-line bg-surface-raised p-4">
      <div className="text-xs text-ink-muted">{live === undefined ? label : <Live on={live}>{label}</Live>}</div>
      <div className={`text-2xl font-semibold tabular-nums ${live ? 'text-brand-600' : 'text-ink'}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-ink-muted">{hint}</div>
      {children}
    </div>
  )
}

export function Today() {
  const campaigns = useAdminCampaigns()
  const agreements = useAgreements()
  const today = useToday()

  const all = campaigns.data?.campaigns ?? []
  const activity = activityById(today.data?.activity)
  const running = all.filter((c) => c.status === 'active' && statusOf(c).tone !== 'closed')
  const open = running.filter((c) => statusOf(c).tone === 'open')
  const sum = (f: (a: Activity) => number) => running.reduce((s, c) => s + (activity.has(c.campaign_id) ? f(activity.get(c.campaign_id)!) : 0), 0)
  const busy = (f: (a: Activity) => number) => running.filter((c) => (activity.get(c.campaign_id) ? f(activity.get(c.campaign_id)!) : 0) > 0).length
  const trend = Array.from({ length: 14 }, (_, i) => sum((a) => a.trend[i] ?? 0))
  const attention = needsYou(all, agreements.data?.agreements ?? [], activity)
  const customers = new Set(all.map((c) => c.org_id)).size
  const walkToday = sum((a) => a.walk_today)
  const phoneToday = sum((a) => a.phone_today)
  // Busiest first: the campaigns doing the most today lead the table.
  const byActivity = [...running].sort((x, y) => {
    const ax = activity.get(x.campaign_id), ay = activity.get(y.campaign_id)
    const n = (a?: Activity) => (a ? a.calling_now + a.walking_now : 0) * 1000 + (a ? a.walk_today + a.phone_today : 0)
    return n(ay) - n(ax)
  })

  const dateLine = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Today</h1>
          <p className="mt-1 text-sm text-ink-muted">{dateLine}</p>
        </div>
        <Link to="/platform/one-off" className={BUTTON}>Get a customer started</Link>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3">
        <Tile label="On the phones now" live={sum((a) => a.calling_now) > 0} value={sum((a) => a.calling_now)}
          hint={`callers across ${busy((a) => a.calling_now)} campaigns`} />
        <Tile label="Out walking now" live={sum((a) => a.walking_now) > 0} value={sum((a) => a.walking_now)}
          hint={`sent results in the last hour · ${busy((a) => a.walking_now)} campaigns`} />
        <Tile label="Results today" value={walkToday + phoneToday} hint={`${walkToday.toLocaleString()} walk · ${phoneToday.toLocaleString()} phone`}>
          <div className="mt-1.5"><Spark values={trend} label="Results per day across the platform, last 14 days" /></div>
        </Tile>
        <Tile label="Campaigns open" value={open.length} hint={`${running.length - open.length} being set up · ${customers} customers`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <section className="flex min-w-0 flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="font-semibold">Needs you</h2>
            {attention.length ? <span className="text-xs text-ink-muted">{attention.length} {attention.length === 1 ? 'thing' : 'things'}</span> : null}
          </div>
          <div className="overflow-hidden rounded-lg border border-line">
            {campaigns.isPending ? (
              <p className="p-4 text-sm text-ink-muted">Loading…</p>
            ) : attention.length === 0 ? (
              <p className="p-4 text-sm text-ink-muted">Nothing waiting on you.</p>
            ) : (
              attention.map((a) => (
                <div
                  key={a.key}
                  className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-l-4 border-b-line px-4 py-2.5 last:border-b-0 ${a.urgent ? 'border-l-gold-500' : 'border-l-brand-400'}`}
                >
                  <div className="min-w-[14rem] flex-1">
                    <div className="text-sm font-semibold">{a.title}</div>
                    <div className="text-xs text-ink-muted">{a.text}</div>
                  </div>
                  <Link
                    to="/platform/campaigns/$campaignId"
                    params={{ campaignId: a.campaignId }}
                    search={{ tab: a.tab }}
                    className="rounded-md border border-brand-600 px-2.5 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-50"
                  >
                    {a.action}
                  </Link>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="flex min-w-0 flex-col gap-2">
          <h2 className="font-semibold">Lately on the platform</h2>
          <ul className="flex flex-col">
            {(today.data?.feed ?? []).slice(0, 10).map((f, i) => (
              <li key={`${f.occurred_at}-${i}`} className="flex gap-3 border-b border-line py-2 text-sm last:border-b-0">
                <time className="w-16 shrink-0 text-xs text-ink-muted tabular-nums" dateTime={f.occurred_at}>{feedTime(f.occurred_at)}</time>
                <span className="min-w-0">
                  <span className="block font-semibold">{f.campaign_name ?? f.org_name ?? 'Platform'}</span>
                  <span className="block break-words text-xs text-ink-muted">{feedText(f)}</span>
                </span>
              </li>
            ))}
            {today.data && today.data.feed.length === 0 ? <li className="text-sm text-ink-muted">Nothing yet.</li> : null}
          </ul>
        </section>
      </div>

      <section className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="font-semibold">Campaigns running</h2>
          <Link to="/platform/campaigns" className="text-sm font-semibold text-brand-700 hover:underline">All campaigns ›</Link>
        </div>
        <CampaignTable campaigns={byActivity} activity={activity} showCustomer />
      </section>
    </div>
  )
}
